import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { createUser, saveFavoriteList } from "../api/getApi"; // Updated import
import { useAuth } from "../context/authContext";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setUser, setIsAuthenticated } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({}); // Changed to an object for easier access
  const [generalError, setGeneralError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Effect to redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const pendingActionData = location.state?.pendingAction?.data;
      const redirectPath = location.state?.redirectAfterLogin || "/";

      if (pendingActionData && (redirectPath === "/" || redirectPath === "/filter")) {
        navigate(redirectPath, {
          replace: true,
          state: { formDataForAutoSubmit: pendingActionData, autoSubmit: true },
        });
      } else {
        navigate(redirectPath, { replace: true, state: location.state });
      }
    }
  }, [isAuthenticated, navigate, location.state]);

  const pendingFavorite = location.state?.pendingFavorite;
  const cameFromRankPredictor = location.state?.from === "rankPredictor";

  // --- Validation Functions ---
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Full Name is required.";
        if (value.trim().length < 3) return "Full Name must be at least 3 characters.";
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return "Name can only contain letters and spaces.";
        return "";
      case "email":
        // Make email required
        if (!value) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
        return "";
      case "mobile":
        // Make mobile required
        if (!value) return "Mobile number is required.";
        if (!/^[6-9]\d{9}$/.test(value)) return "Please enter a valid 10-digit mobile number.";
        return "";
      case "password":
        if (!value) return "Password is required.";
        if (value.length < 6) return "Password must be at least 6 characters long.";
        return "";
      case "confirmPassword":
        if (value !== formData.password) return "Passwords do not match.";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (generalError) setGeneralError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");
    let validationErrors = {};

    // Validate all fields
    Object.keys(formData).forEach(name => {
      const error = validateField(name, formData[name]);
      if (error) validationErrors[name] = error;
    });

    // The check for either email or mobile is no longer needed

    if (!acceptedTerms) {
      validationErrors.terms = "You must accept the Terms and Conditions.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setGeneralError("Please correct the errors below.");
      return;
    }

    setIsLoading(true);
    try {
      const { confirmPassword, ...userData } = formData; // Exclude confirmPassword from API call
      const createResponse = await createUser(userData);

      if (!createResponse || !createResponse.success) {
        // This case is for non-axios errors or if backend returns success: false
        throw new Error(createResponse?.error || "Failed to create account.");
      }

      // Set auth context to log the user in
      setUser(createResponse.user);
      setIsAuthenticated(true);

      // Post-signup actions (e.g., saving pending favorites)
      if (pendingFavorite?.colleges?.length > 0) {
        await saveFavoriteList(pendingFavorite);
      }
      // The useEffect watching `isAuthenticated` will handle the final navigation.

    } catch (error) {
      // Handle errors from the backend
      const data = error.response?.data;
      if (data) {
        if (data.errors) { // Handle validation errors from express-validator
          const backendErrors = data.errors.reduce((acc, err) => {
            acc[err.path] = err.msg;
            return acc;
          }, {});
          setErrors(backendErrors);
          setGeneralError("Please correct the validation errors.");
        } else if (data.error) { // Handle general backend errors
          setGeneralError(data.error);
        } else {
          setGeneralError("An unknown error occurred. Please try again.");
        }
      } else {
        // Handle network errors or other issues
        setGeneralError("Could not connect to the server. Please check your network.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Create an account</h2>
              <p className="text-sm text-gray-600 mt-1">
                {cameFromRankPredictor
                  ? "Sign up to see your rank prediction and save your results."
                  : "Sign up to find your perfect college match"}
              </p>
            </div>

            <div className="p-6">
              {/* General Error Display */}
              {generalError && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
                  <p className="text-sm font-medium">Error</p>
                  <p className="text-sm">{generalError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.name ? "border-red-500" : "border-gray-300"}`} />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Email Field - No longer optional */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                  <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.email ? "border-red-500" : "border-gray-300"}`} />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                {/* Mobile Field - No longer optional */}
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">Mobile Number</label>
                  <input id="mobile" name="mobile" type="tel" required maxLength="10" value={formData.mobile} onChange={handleChange} className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.mobile ? "border-red-500" : "border-gray-300"}`} />
                  {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password"className="block text-sm font-medium text-gray-700">Password</label>
                  <input id="password" name="password" type="password" required value={formData.password} onChange={handleChange} className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.password ? "border-red-500" : "border-gray-300"}`} />
                  {errors.password ? <p className="mt-1 text-sm text-red-600">{errors.password}</p> : <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters long.</p>}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword"className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <input id="confirmPassword" name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${errors.confirmPassword ? "border-red-500" : "border-gray-300"}`} />
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input id="terms" name="terms" type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className={`h-4 w-4 text-blue-600 border-gray-300 rounded ${errors.terms ? 'border-red-500' : ''}`} />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-gray-600">I agree to the <Link to="/terms" target="_blank" className="text-blue-600 hover:underline">Terms and Conditions</Link></label>
                    {errors.terms && <p className="text-sm text-red-600">{errors.terms}</p>}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg disabled:opacity-75">
                    {isLoading ? "Creating account..." : "Create Account"}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link to="/login" state={location.state} className="font-medium text-blue-600 hover:text-blue-500">Login</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;


